import React, { useEffect, useState, useCallback } from 'react';
import { Box, Stack, Chip, Typography, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { Grid } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { getCreditLogs } from '@services/creditLogService';
import type { CreditLog } from '@app-types/models/finance';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import TollIcon from '@mui/icons-material/Toll';
import FilterListIcon from '@mui/icons-material/FilterList';
import PeopleIcon from '@mui/icons-material/People';
import DateRangeIcon from '@mui/icons-material/DateRange';
import { PageContainer, PageHeader, SectionCard, EmptyState, ResponsiveTable, StatCard } from '../components/ui';
import type { Column } from '../components/ui';
import { brand } from '../theme/tokens';
dayjs.extend(isBetween);

const CreditLogPageNew: React.FC = () => {
  const { t } = useTranslation();
  const [creditLogs, setCreditLogs] = useState<CreditLog[]>([]);
  const [filter, setFilter] = useState<string>('all');
  const [stylistFilter, setStylistFilter] = useState<string>('all');
  const [startDate, setStartDate] = useState<Dayjs>(dayjs().subtract(30, 'day'));
  const [endDate, setEndDate] = useState<Dayjs>(dayjs());
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const fetchCreditLogs = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getCreditLogs();
      setCreditLogs(Array.isArray(response.creditLogs) ? response.creditLogs : []);
    } catch (error) {
      console.error('Error fetching credit logs:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCreditLogs();
  }, [fetchCreditLogs]);

  const handleFilterChange = (newFilter: string) => {
    setFilter(newFilter);
  };

  const handleStylistFilterChange = (newStylistFilter: string) => {
    setStylistFilter(newStylistFilter);
  };

  const filteredLogs = creditLogs.filter((log) => {
    const filterCondition = filter === 'all' ? true : log?.type === filter;
    const stylistCondition = stylistFilter === 'all' ? true : log?.staffId?._id === stylistFilter;
    const dateCondition = dayjs(log?.createdAt).isBetween(startDate, endDate, null, '[]');
    return filterCondition && stylistCondition && dateCondition;
  });

  const uniqueStylistIds = [...new Set(creditLogs.map(log => log?.staffId?._id).filter((id): id is string => id !== undefined))];
  console.log('uniqueStylistIds---', uniqueStylistIds);

  // --- Revamp helpers ---
  const totalAdded = filteredLogs
    .filter((log) => log?.type === 'add')
    .reduce((sum, log) => sum + (log?.amount ?? 0), 0);
  const totalDeducted = filteredLogs
    .filter((log) => log?.type === 'deduct')
    .reduce((sum, log) => sum + (log?.amount ?? 0), 0);

  const stylistLabel = (stylistId: string) =>
    creditLogs.find((log) => log?.staffId?._id === stylistId)?.staffId?.displayName ||
    creditLogs.find((log) => log?.staffId?._id === stylistId)?.staffId?.email ||
    stylistId;

  const columns: Column<CreditLog>[] = [
    {
      key: 'reason',
      header: t('creditLogPage.reason'),
      primary: true,
      render: (log) => log?.code || t('creditLogPage.na'),
    },
    {
      key: 'type',
      header: t('creditLogPage.type'),
      render: (log) => (
        <Chip
          size="small"
          label={log?.type === 'add' ? t('creditLogPage.addition') : t('creditLogPage.deduction')}
          sx={{
            fontWeight: 600,
            color: log?.type === 'add' ? brand.success : brand.danger,
            bgcolor: log?.type === 'add' ? brand.successSoft : brand.dangerSoft,
          }}
        />
      ),
    },
    {
      key: 'amount',
      header: t('creditLogPage.amount'),
      align: 'right',
      render: (log) => (
        <Typography
          component="span"
          variant="body2"
          sx={{ fontWeight: 700, color: log?.type === 'add' ? brand.success : brand.danger }}
        >
          {log?.type === 'add' ? '+' : '-'}{log?.amount?.toFixed(2)}
        </Typography>
      ),
    },
    {
      key: 'balanceBefore',
      header: t('creditLogPage.balanceBefore'),
      align: 'right',
      render: (log) => log?.balanceBefore?.toFixed(2) || t('creditLogPage.na'),
    },
    {
      key: 'balanceAfter',
      header: t('creditLogPage.balanceAfter'),
      align: 'right',
      render: (log) => log?.balanceAfter?.toFixed(2),
    },
    {
      key: 'stylist',
      header: t('creditLogPage.stylist'),
      render: (log) => log?.staffId?.displayName || log?.staffId?.email,
    },
    {
      key: 'date',
      header: t('creditLogPage.date'),
      render: (log) => dayjs(log?.createdAt).format('DD/MM/YYYY HH:mm'),
    },
  ];

  return (
    <PageContainer>
      <PageHeader
        title={t('creditLogPage.title')}
        subtitle={t('creditLogPage.subtitle', 'Audit every credit added or deducted across your team.')}
        icon={<ReceiptLongIcon />}
        classicPath="/classic/credit-logs"
      />

      {/* Summary */}
      <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
        <Grid size={{ xs: 6, sm: 4 }}>
          <StatCard
            tone="sage"
            icon={<TollIcon />}
            label={t('creditLogPage.totalAdded', 'Total added')}
            value={totalAdded.toFixed(2)}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 4 }}>
          <StatCard
            tone="rust"
            icon={<TollIcon />}
            label={t('creditLogPage.totalDeducted', 'Total deducted')}
            value={totalDeducted.toFixed(2)}
          />
        </Grid>
      </Grid>

      {/* Filters */}
      <Box sx={{ mb: 2.5 }}>
        <SectionCard title={t('creditLogPage.filters', 'Filters')} icon={<FilterListIcon />}>
          <Stack spacing={2}>
            {/* Type */}
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                {t('creditLogPage.type')}
              </Typography>
              <ToggleButtonGroup
                exclusive
                value={filter}
                onChange={(_e, value) => { if (value !== null) handleFilterChange(value); }}
                sx={{ flexWrap: 'wrap', '& .MuiToggleButton-root': { minHeight: 40, textTransform: 'none' } }}
              >
                <ToggleButton value="all">{t('creditLogPage.all')}</ToggleButton>
                <ToggleButton value="add">{t('creditLogPage.additions')}</ToggleButton>
                <ToggleButton value="deduct">{t('creditLogPage.deductions')}</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* Stylist */}
            <Box>
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1 }}>
                <PeopleIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  {t('creditLogPage.stylist')}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                <Chip
                  label={t('creditLogPage.allStylists')}
                  clickable
                  onClick={() => handleStylistFilterChange('all')}
                  color={stylistFilter === 'all' ? 'primary' : 'default'}
                  variant={stylistFilter === 'all' ? 'filled' : 'outlined'}
                  sx={{ minHeight: 40 }}
                />
                {uniqueStylistIds.map((stylistId) => (
                  <Chip
                    key={stylistId}
                    label={stylistLabel(stylistId)}
                    clickable
                    onClick={() => handleStylistFilterChange(stylistId)}
                    color={stylistFilter === stylistId ? 'primary' : 'default'}
                    variant={stylistFilter === stylistId ? 'filled' : 'outlined'}
                    sx={{ minHeight: 40 }}
                  />
                ))}
              </Stack>
            </Box>

            {/* Date range */}
            <Box>
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1 }}>
                <DateRangeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  {t('creditLogPage.dateRange', 'Date range')}
                </Typography>
              </Stack>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                  <DatePicker
                    label={t('creditLogPage.startDate', 'Start Date')}
                    value={startDate}
                    onChange={(newValue: Dayjs | null) => { if (newValue) setStartDate(newValue); }}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                  <DatePicker
                    label={t('creditLogPage.endDate', 'End Date')}
                    value={endDate}
                    onChange={(newValue: Dayjs | null) => { if (newValue) setEndDate(newValue); }}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Stack>
              </LocalizationProvider>
            </Box>
          </Stack>
        </SectionCard>
      </Box>

      {/* Table */}
      <ResponsiveTable<CreditLog>
        columns={columns}
        rows={filteredLogs}
        getRowKey={(log) => log._id}
        loading={isLoading}
        emptyState={
          <EmptyState
            icon={<ReceiptLongIcon />}
            title={t('creditLogPage.emptyTitle', 'No credit activity')}
            description={t(
              'creditLogPage.emptyDescription',
              'No credit was added or deducted for the selected filters. Try widening the date range or clearing filters.',
            )}
          />
        }
      />
    </PageContainer>
  );
};

export default CreditLogPageNew;
